import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";

/** `ui/public/licences.json`, written by tools/licences.py. */
interface Licences {
  avgv: string;
  components: {
    name: string;
    version: string;
    kind: string;
    licence: string;
    url: string;
    texts: string[];
    note?: string;
  }[];
  texts: Record<string, string>;
  /** The LGPL, GPL and MPL components and where their source is (licence review L4). */
  source_offer: {
    text: string;
    components: {
      name: string;
      version: string;
      licence: string;
      source: string;
    }[];
  };
}

export function AboutPage() {
  const about = useQuery({
    queryKey: ["about"],
    queryFn: () => unwrap(api.GET("/api/about")),
  });
  const a = about.data;
  return (
    <Stack spacing={3} component="section" aria-label="About AVGV">
      <Typography variant="h5" component="h2">
        About AVGV
      </Typography>
      {about.isError && <Alert severity="error">{about.error.message}</Alert>}
      {a && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Table size="small" aria-label="Versions">
            <TableBody>
              {[
                ["AVGV", `${a.version} (${a.edition})`],
                ["Build", a.build],
                ["Database", `${a.database}, schema ${a.schema}`],
                ["Media", a.gstreamer ?? "No media worker running"],
                ["Speech detection", a.speech ?? "No media worker running"],
                ["Speech to text", a.transcriber ?? "Not installed"],
              ].map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell component="th" scope="row" sx={{ width: 180 }}>
                    {k}
                  </TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>{v}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            © Atlantic Technology. AVGV includes the third-party software
            listed below, under the licences shown.
          </Typography>
        </Paper>
      )}
      <ThirdParty />
    </Stack>
  );
}

function ThirdParty() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [kind, setKind] = useState("");
  const [shown, setShown] = useState<string | null>(null);
  // Several megabytes of licence texts: fetched only when asked for.
  const licences = useQuery({
    queryKey: ["licences"],
    enabled: open,
    staleTime: Infinity,
    queryFn: async (): Promise<Licences> => {
      const r = await fetch("/licences.json");
      if (!r.ok) throw new Error(`licences.json: ${r.status}`);
      return (await r.json()) as Licences;
    },
  });
  const kinds = useMemo(
    () => [...new Set(licences.data?.components.map((c) => c.kind))],
    [licences.data],
  );
  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (licences.data?.components ?? []).filter(
      (c) =>
        (!kind || c.kind === kind) &&
        (!f ||
          c.name.toLowerCase().includes(f) ||
          c.licence.toLowerCase().includes(f)),
    );
  }, [licences.data, filter, kind]);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Third-party software"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Third-party software
      </Typography>
      {!open && (
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Show licences
        </Button>
      )}
      {licences.isPending && open && <CircularProgress aria-label="Loading" />}
      {licences.isError && (
        <Alert severity="error">{licences.error.message}</Alert>
      )}
      {licences.data && (
        <Stack spacing={2}>
          <SourceOffer offer={licences.data.source_offer} />
          <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Find"
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              helperText="Part of a name or licence"
            />
            <TextField
              select
              label="Kind"
              size="small"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {kinds.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {rows.length} of {licences.data.components.length}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Components">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Licence</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => {
                  const key = `${c.kind} ${c.name} ${c.version}`;
                  return (
                    <Row
                      key={key}
                      c={c}
                      open={shown === key}
                      onToggle={() => setShown(shown === key ? null : key)}
                      texts={licences.data.texts}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}

function SourceOffer({ offer }: { offer: Licences["source_offer"] }) {
  const [open, setOpen] = useState(false);
  return (
    <Box component="section" aria-label="Source code offer">
      <Button size="small" onClick={() => setOpen(!open)} aria-expanded={open}>
        {open ? "Hide" : "Show"} the source code offer (
        {offer.components.length} components)
      </Button>
      {open && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {offer.text}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Source code">
              <TableBody>
                {offer.components.map((c) => (
                  <TableRow key={`${c.name} ${c.version}`}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.version}</TableCell>
                    <TableCell sx={{ overflowWrap: "anywhere" }}>
                      {c.licence}
                    </TableCell>
                    <TableCell sx={{ overflowWrap: "anywhere" }}>
                      <Link
                        href={c.source.split(" ")[0]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {c.source}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function Row({
  c,
  open,
  onToggle,
  texts,
}: {
  c: Licences["components"][number];
  open: boolean;
  onToggle: () => void;
  texts: Licences["texts"];
}) {
  return (
    <>
      <TableRow>
        <TableCell>
          <Link href={c.url} target="_blank" rel="noreferrer">
            {c.name}
          </Link>
          {c.note && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {c.note}
            </Typography>
          )}
        </TableCell>
        <TableCell>{c.version}</TableCell>
        <TableCell>{c.kind}</TableCell>
        <TableCell sx={{ overflowWrap: "anywhere" }}>{c.licence}</TableCell>
        <TableCell>
          <Button
            size="small"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} the licence of ${c.name}`}
          >
            {open ? "Hide" : "Text"}
          </Button>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={5}>
            {c.texts.map((t) => (
              <Box
                key={t}
                component="pre"
                sx={{
                  m: 0,
                  mb: 1,
                  maxHeight: 360,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  fontSize: 12,
                }}
              >
                {texts[t]}
              </Box>
            ))}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
